import { useState, useEffect } from 'react';
import { Facebook, Twitter, Instagram, Youtube, Linkedin } from 'lucide-react';
import { db } from '@/lib/firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { useLanguage } from '@/contexts/LanguageContext';

interface SocialLink {
  platform: string;
  url: string;
}

type BilingualField = string | { ar: string; en: string };

interface QuickLink {
  text: BilingualField;
  url: string;
}

interface FooterData {
  logo: string;
  logoAlt: BilingualField;
  tagline: BilingualField;
  quickLinks: {
    title: BilingualField;
    links: QuickLink[];
  };
  contact: {
    title: BilingualField;
    phone: string;
    email: string;
    address: BilingualField;
  };
  social: {
    title: BilingualField;
    links: SocialLink[];
  };
  copyright: BilingualField;
}

const socialIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  twitter: Twitter,
  facebook: Facebook,
  instagram: Instagram,
  youtube: Youtube,
  linkedin: Linkedin,
};

export function LandingFooter() {
  const [footerData, setFooterData] = useState<FooterData | null>(null);
  const [loading, setLoading] = useState(true);
  const { t, language } = useLanguage();

  const toBilingual = (val: any): { ar: string; en: string } => {
    if (!val) return { ar: '', en: '' };
    if (typeof val === 'string') return { ar: val, en: '' };
    return { ar: val.ar || '', en: val.en || '' };
  };

  const localize = (field: BilingualField): string => {
    if (typeof field === 'string') return field;
    return field[language] || field.ar || field.en || '';
  };

  useEffect(() => {
    fetchFooterData();
  }, []);

  const fetchFooterData = async () => {
    try {
      const docRef = doc(db, 'landing_footer', 'info');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        setFooterData({
          logo: data.logo || '',
          logoAlt: toBilingual(data.logoAlt),
          tagline: toBilingual(data.tagline),
          quickLinks: {
            title: toBilingual(data.quickLinks?.title),
            links: (data.quickLinks?.links || []).map((link: any) => ({
              text: toBilingual(link.text),
              url: link.url || '',
            })),
          },
          contact: {
            title: toBilingual(data.contact?.title),
            phone: data.contact?.phone || '',
            email: data.contact?.email || '',
            address: toBilingual(data.contact?.address),
          },
          social: {
            title: toBilingual(data.social?.title),
            links: data.social?.links || [],
          },
          copyright: toBilingual(data.copyright),
        });
      }
    } catch (err) {
      console.error('Error fetching footer data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <footer className="bg-[#004250] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">{t('landing.common.loading')}</div>
        </div>
      </footer>
    );
  }

  if (!footerData) {
    return null;
  }

  return (
    <footer className="bg-[#004250] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-4 gap-8">
          <div>
            <a href="https://namat.org.sa" className="block mb-4 pt-[50px]">
              <img src={footerData.logo} alt={localize(footerData.logoAlt)} className="h-28 w-auto" />
            </a>
            <p className="text-gray-300 text-[19px] pr-1">{localize(footerData.tagline)}</p>
          </div>
          <div>
            <h3 className="text-xl font-bold mb-4">{localize(footerData.quickLinks.title)}</h3>
            <ul className="space-y-2">
              {footerData.quickLinks.links.map((link, index) => (
                <li key={index}>
                  <a href={link.url} className="hover:text-[#7dda55]">
                    {localize(link.text)}
                  </a>
                </li>
              ))}
              <li>
                <a href="/delete-account" className="hover:text-[#7dda55] transition-colors">
                  {language === 'ar' ? 'طلب حذف الحساب' : 'Delete Account'}
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-xl font-bold mb-4">{localize(footerData.contact.title)}</h3>
            <ul className="space-y-2">
              <li>{t('landing.footer.phone')}: {footerData.contact.phone}</li>
              <li>{t('landing.footer.emailLabel')}: {footerData.contact.email}</li>
              <li>{localize(footerData.contact.address)}</li>
            </ul>
          </div>
          <div>
            <h3 className="text-xl font-bold mb-4">{localize(footerData.social.title)}</h3>
            <div className="flex space-x-4">
              {footerData.social.links.map((link, index) => {
                const Icon = socialIcons[link.platform];
                return Icon ? (
                  <a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#7dda55] hover:text-[#6bc748] transition-colors"
                  >
                    <Icon className="w-6 h-6" />
                  </a>
                ) : null;
              })}
            </div>
          </div>
        </div>
        <div className="mt-8 pt-8 text-center">
          <p>© {new Date().getFullYear()} {localize(footerData.copyright)}</p>
        </div>
      </div>
    </footer>
  );
}

